import React, { useState, useEffect } from "react";
import axios from "axios";
import { Check, X, Edit2, Trash2, Plus } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const DesignationService = {
  getDesignations: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/designations`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching designations:", error);
      throw error;
    }
  },
  addDesignation: async (designation, companyId, token, userType) => {
    try {
      const response = await axios.post(`${API_URL}/designations`, designation, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error adding designation:", error);
      throw error;
    }
  },
  updateDesignation: async (id, designation, companyId, token, userType) => {
    try {
      const response = await axios.put(`${API_URL}/designations/${id}`, designation, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error updating designation:", error);
      throw error;
    }
  },
  deleteDesignation: async (id, companyId, token, userType) => {
    try {
      await axios.delete(`${API_URL}/designations/${id}`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
    } catch (error) {
      console.error("Error deleting designation:", error);
      throw error;
    }
  }
};

const Designation = () => {
  const { user, userType, companyId, logout } = useAuth();
  const [designations, setDesignations] = useState([]);
  const [newDesignation, setNewDesignation] = useState("");
  const [newHierarchyLevel, setNewHierarchyLevel] = useState(1);
  const [newLeaveAllocation, setNewLeaveAllocation] = useState({
    sickLeave: 7,
    casualLeave: 7,
    earnedLeave: 14
  });
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editLevel, setEditLevel] = useState(1);
  const [editLeaveAllocation, setEditLeaveAllocation] = useState({
    sickLeave: 7,
    casualLeave: 7,
    earnedLeave: 14
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (companyId) {
      fetchDesignations();
    } else {
      logout();
    }
  }, [companyId]);

  const fetchDesignations = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await DesignationService.getDesignations(companyId, token, userType);
      setDesignations(data);
      setError(null);
    } catch (error) {
      setError("Error fetching designations: " + (error.response?.data?.error || error.message));
    }
  };

  const handleAddDesignation = async (e) => {
    e.preventDefault();
    
    if (!newDesignation.trim()) {
      setError("Designation name cannot be empty");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await DesignationService.addDesignation({
        name: newDesignation,
        hierarchyLevel: Number(newHierarchyLevel),
        leaveAllocation: {
          sickLeave: Number(newLeaveAllocation.sickLeave),
          casualLeave: Number(newLeaveAllocation.casualLeave),
          earnedLeave: Number(newLeaveAllocation.earnedLeave)
        }
      }, companyId, token, userType);
      
      setNewDesignation("");
      setNewHierarchyLevel(1);
      setNewLeaveAllocation({
        sickLeave: 7,
        casualLeave: 7,
        earnedLeave: 14
      });
      setSuccess("Designation added successfully!");
      setError(null);
      fetchDesignations();
    } catch (error) {
      setError("Error adding designation: " + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (designation) => {
    setEditId(designation.id);
    setEditName(designation.name);
    setEditLevel(designation.hierarchyLevel);
    setEditLeaveAllocation({
      sickLeave: designation.leaveAllocation?.sickLeave || 7,
      casualLeave: designation.leaveAllocation?.casualLeave || 7,
      earnedLeave: designation.leaveAllocation?.earnedLeave || 14
    });
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setEditName("");
    setEditLevel(1);
    setEditLeaveAllocation({
      sickLeave: 7,
      casualLeave: 7,
      earnedLeave: 14
    });
    setError(null);
  };

  const handleUpdateDesignation = async (id) => {
    if (!editName.trim()) {
      setError("Designation name cannot be empty");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await DesignationService.updateDesignation(id, {
        name: editName,
        hierarchyLevel: Number(editLevel),
        leaveAllocation: {
          sickLeave: Number(editLeaveAllocation.sickLeave),
          casualLeave: Number(editLeaveAllocation.casualLeave),
          earnedLeave: Number(editLeaveAllocation.earnedLeave)
        }
      }, companyId, token, userType);
      
      setEditId(null);
      setSuccess("Designation updated successfully!");
      setError(null);
      fetchDesignations();
    } catch (error) {
      setError("Error updating designation: " + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteDesignation = async (id) => {
    if (window.confirm("Are you sure you want to delete this designation?")) {
      try {
        const token = localStorage.getItem('token');
        await DesignationService.deleteDesignation(id, companyId, token, userType);
        setSuccess("Designation deleted successfully!");
        setError(null);
        fetchDesignations();
      } catch (error) {
        setError("Error deleting designation: " + (error.response?.data?.error || error.message));
      }
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-r from-blue-900 to-blue-700">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-6 text-center">
          Designation Management
        </h1>

        {companyId ? (
          <>
            <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
              <h2 className="text-2xl font-bold mb-4">Add New Designation</h2>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-6">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="bg-green-50 border border-green-200 text-green-600 p-4 rounded-lg mb-6">
                  {success}
                </div>
              )}

              <form onSubmit={handleAddDesignation} className="space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-grow">
                    <label className="block mb-1 text-sm">Designation Name</label>
                    <input
                      type="text"
                      value={newDesignation}
                      onChange={(e) => setNewDesignation(e.target.value)}
                      className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter designation name (e.g., CEO, Manager)"
                    />
                  </div>
                  
                  <div className="md:w-1/4">
                    <label className="block mb-1 text-sm">Hierarchy Level</label>
                    <input
                      type="number"
                      value={newHierarchyLevel}
                      onChange={(e) => setNewHierarchyLevel(e.target.value)}
                      min="1"
                      className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Level (1=top)"
                    />
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium mb-3">Leave Allocation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block mb-1 text-sm">Sick Leave (days/year)</label>
                      <input
                        type="number"
                        value={newLeaveAllocation.sickLeave}
                        onChange={(e) => setNewLeaveAllocation({
                          ...newLeaveAllocation,
                          sickLeave: e.target.value
                        })}
                        min="0"
                        className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-1 text-sm">Casual Leave (days/year)</label>
                      <input
                        type="number"
                        value={newLeaveAllocation.casualLeave}
                        onChange={(e) => setNewLeaveAllocation({
                          ...newLeaveAllocation,
                          casualLeave: e.target.value
                        })}
                        min="0"
                        className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-1 text-sm">Earned Leave (days/year)</label>
                      <input
                        type="number"
                        value={newLeaveAllocation.earnedLeave}
                        onChange={(e) => setNewLeaveAllocation({
                          ...newLeaveAllocation,
                          earnedLeave: e.target.value
                        })}
                        min="0"
                        className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <button 
                    type="submit"
                    className="bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Plus size={18} />
                    Add Designation
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold mb-4">Designations</h2>
              
              <div className="w-full overflow-auto max-h-[70vh]">
                <table className="w-full min-w-max">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-4 text-left">Designation</th>
                      <th className="p-4 text-left">Hierarchy</th>
                      <th className="p-4 text-center">Sick Leave</th>
                      <th className="p-4 text-center">Casual Leave</th>
                      <th className="p-4 text-center">Earned Leave</th>
                      <th className="p-4 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {designations.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-4 text-center text-gray-500">
                          No designations found
                        </td>
                      </tr>
                    ) : (
                      designations.map((designation) => (
                        <tr key={designation.id} className="border-t hover:bg-gray-50">
                          <td className="p-4">
                            {editId === designation.id ? (
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              designation.name
                            )}
                          </td>
                          <td className="p-4">
                            {editId === designation.id ? (
                              <input
                                type="number"
                                value={editLevel}
                                onChange={(e) => setEditLevel(e.target.value)}
                                min="1"
                                className="border p-2 w-24 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              designation.hierarchyLevel
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {editId === designation.id ? (
                              <input
                                type="number"
                                value={editLeaveAllocation.sickLeave}
                                onChange={(e) => setEditLeaveAllocation({
                                  ...editLeaveAllocation,
                                  sickLeave: e.target.value
                                })}
                                min="0"
                                className="border p-2 w-20 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              designation.leaveAllocation?.sickLeave || "-"
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {editId === designation.id ? (
                              <input
                                type="number"
                                value={editLeaveAllocation.casualLeave}
                                onChange={(e) => setEditLeaveAllocation({
                                  ...editLeaveAllocation,
                                  casualLeave: e.target.value
                                })}
                                min="0"
                                className="border p-2 w-20 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              designation.leaveAllocation?.casualLeave || "-"
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {editId === designation.id ? (
                              <input
                                type="number"
                                value={editLeaveAllocation.earnedLeave}
                                onChange={(e) => setEditLeaveAllocation({
                                  ...editLeaveAllocation,
                                  earnedLeave: e.target.value
                                })}
                                min="0"
                                className="border p-2 w-20 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              designation.leaveAllocation?.earnedLeave || "-"
                            )}
                          </td>
                          <td className="p-4">
                            {editId === designation.id ? (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleUpdateDesignation(designation.id)}
                                  className="p-2 text-green-600 hover:text-green-800 transition-colors"
                                  title="Save changes"
                                >
                                  <Check size={18} />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
                                  title="Cancel edit"
                                >
                                  <X size={18} />
                                </button>
                              </div>
                            ) : (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEdit(designation)}
                                  className="p-2 text-blue-600 hover:text-blue-800 transition-colors"
                                  title="Edit designation"
                                >
                                  <Edit2 size={18} />
                                </button>
                                <button
                                  onClick={() => handleDeleteDesignation(designation.id)}
                                  className="p-2 text-red-600 hover:text-red-800 transition-colors"
                                  title="Delete designation"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </>
        ) : (
          <div className="text-center text-lg text-white">
            Company information not available. Please login again.
          </div>
        )}
      </div>
    </div>
  );
};

export default Designation;